<template>
    <div>
        <design-2021-apprentice-data-table
            class="module-participant-table"
            v-if="module"
            :fields="fields"
            :items="users">
            <template
                v-slot:options
                v-if="module.module_dates && module.module_dates.length">
                <b-form-group
                    class="group-module-select"
                    label="Module"
                    label-for="module-selector">
                    <b-form-select
                        class="apprentice-primary-select module-filter"
                        id="module-selector"
                        v-model="selectedModule"
                        :options="modules"
                        value-field="id"
                        text-field="title"
                        @change="showSelectedModule">
                    </b-form-select>
                </b-form-group>

                <b-form-group
                    class="group-period-select"
                    :label="$t('modules.period')"
                    label-for="module-date-selector">
                    <b-form-select
                        class="apprentice-primary-select module-period-filter"
                        id="module-date-selector"
                        v-model="selectedperiod"
                        :options="periods"
                        value-field="id"
                        text-field="title"
                        :disabled="periods.length <= 2">
                    </b-form-select>
                </b-form-group>

                <b-button
                    class="btn-add btn-add-share"
                    v-b-modal="'module-management-add-users-modal'"
                    variant="primary">
                    {{ $t('common.add') }}
                </b-button>
            </template>

            <template #cell(periods)="data">
                <div class="periods">
                    {{ gtePeriodName(data.item.module_date_id) }}
                </div>
            </template>

            <template v-slot:cell(motivation)="row">
                <b-button
                    class="btn-comments-fad btn-show-module-motivation remove-icon-margin"
                    v-if="row.item.motivation"
                    v-b-tooltip.hover
                    variant="primary-icon-only"
                    :title="$t('modules.view_motivation')"
                    @click="motivation(row.item)">
                </b-button>

                <b-button
                    class="btn-comment btn-show-module-motivation remove-icon-margin btn-icon-only"
                    v-else>
                </b-button>
            </template>

            <template v-slot:cell(actions)="row">
                <b-button
                    class="btn-remove"
                    variant="danger-icon-only"
                    @click="removeParticipant(row.item)"></b-button>
            </template>

            <template #empty>
                <div class="not-found with-header participant-list-empty">
                    <div class="icon">
                        <i class="fas fa-exclamation-circle"></i>
                    </div>
                    <div class="alert-notice">{{ $t('modules.no_participants_found') }}</div>
                </div>
            </template>
        </design-2021-apprentice-data-table>

        <design-2021-module-management-edit-participant-motivation-modal-component :ref="'module-show-motivation'">
        </design-2021-module-management-edit-participant-motivation-modal-component>

        <design-2021-module-management-edit-participant-add-users-modal-component
            :module-in="module"
            :selected-date-id="getSelectedPeriodModuleDate"
            :selected-user-ids="userIds"
            :module-periods-in="modulePeriodsIn">
        </design-2021-module-management-edit-participant-add-users-modal-component>
    </div>
</template>

<script>
import { ords } from '../../../app';

const moduleOrds = ords.use('module');

export default {
    name: 'design-2021-module-management-edit-participant-component',
    props: {
        moduleIn: {
            type: Object,
            required: true,
            default: () => {},
        },

        modulePeriodsIn: {
            type: Array,
            required: true,
            default: () => [],
        },
    },

    data() {
        return {
            selected_date_id: null,
            fields: [
                {
                    key: 'user.full_name',
                    label: this.$t('common.name'),
                },
                {
                    key: 'periods',
                    label: this.$t('modules.period'),
                },
                {
                    key: 'motivation',
                    label: this.$t('modules.motivation'),
                },
                {
                    key: 'actions',
                    label: this.$t('common.actions'),
                },
            ],
            module: null,
            userIds: [],
            modules: [],
            selectedModule: this.moduleIn.id,
            selectedperiod: null,
        };
    },

    mounted() {
        moduleOrds.add(this.moduleIn);

        moduleOrds
            .with('module_dates', function (q) {
                q.with('module_users', (userCallback) => {
                    userCallback.with('user');
                });
                q.with('module_period', (modulePeriodsCallback) => {
                    modulePeriodsCallback.with('period');
                });
            })
            .find(this.moduleIn.id)
            .subscribe((module) => {
                this.module = module;
            });

        this.selected_date_id = this.module.module_dates.length ? this.module.module_dates[0].id : null;

        this.axios.get(this.routes.getModules()).then(
            (response) => {
                this.modules = response.data;
                this.modules.unshift({
                    key: null,
                    title: 'Kies een module',
                    disabled: true,
                });
            },

            (error) => {
                this.alert.ajaxError(error);
            },
        );
    },

    watch: {
        moduleIn: function (module) {
            if (!this.selected_date_id) {
                return;
            }
            for (const date of module.module_dates) {
                if (date.id === this.selected_date_id) {
                    return;
                }
            }
            this.selected_date_id = null;
        },
    },

    computed: {
        users() {
            let users = [];
            this.module.module_dates.forEach((date) => {
                users.push(...date.module_users);
            });

            users = [...new Map(users.map((user) => [user['user_id'], user])).values()];

            users = users.map((user) => {
                for (let i = 0; i < this.module.module_dates.length; i++) {
                    if (user.module_date_id === this.module.module_dates[i].id) {
                        user.module_period_id = this.module.module_dates[i].module_period_id;
                        continue;
                    }
                }

                return user;
            });

            if (this.selectedperiod !== null) {
                return users.filter((user) => user.module_period_id === this.selectedperiod);
            }

            if (users.length > 0) {
                this.userIds = users.map((user) => user.user_id);
            }

            return users;
        },

        periods() {
            let allPeriods = [
                {
                    id: null,
                    title: this.$t('modules.all_periods'),
                },
            ];

            let module_periods = this.modulePeriodsIn
                .map((period) => {
                    this.module.module_dates.forEach((date) => {
                        if (period.id === date.module_period_id) {
                            period.module_date_id = date.id;
                        }
                    });
                    return period;
                })
                .filter((module_period) => {
                    let found = false;
                    this.module.module_dates.forEach((date) => {
                        if (module_period.id === date.module_period_id) {
                            found = true;
                        }
                    });

                    if (found) {
                        return module_period;
                    }
                });

            allPeriods.push(...module_periods);

            if (allPeriods.length === 2) {
                this.selectedperiod = allPeriods[1].id;
            }

            return allPeriods;
        },

        getSelectedPeriodModuleDate() {
            if (!this.selectedperiod) {
                return null;
            }

            return this.modulePeriodsIn
                .filter((period) => period.id === this.selectedperiod)
                .map((period) => period.module_date_id)
                .pop();
        },
    },

    methods: {
        motivation(module) {
            this.$refs[`module-show-motivation`].show(module);
        },

        removeParticipant(moduleUser) {
            this.alert
                .confirm(this.$t('modules.delete_user'), this.$t('modules.are_you_sure_you_want_to_delete_this_user'))
                .then((confirm) => {
                    if (!confirm) {
                        return;
                    }

                    this.alert.saving();
                    this.axios.delete(this.routes.deleteModuleUser(moduleUser.id)).then(
                        (response) => {
                            moduleUser.remove();
                            this.alert.success();
                        },
                        (error) => {
                            this.alert.ajaxError(error);
                        },
                    );
                });
        },

        showSelectedModule() {
            window.location.href = this.routes.showModule(this.selectedModule);
        },

        gtePeriodName(dateId) {
            const date = this.moduleIn.module_dates.find((date) => date.id === dateId);

            if (!date) {
                return null;
            }

            return date.module_period.period.title;
        },
    },
};
</script>

<style scoped lang="scss">
select.custom-select {
    margin-bottom: 0;
}

.module-participant-table ::v-deep {
    .options {
        margin-top: map-get($grid-gutter-spacing, 'twenty-one');
        margin-bottom: $grid-gutter-width;
        row-gap: map-get($grid-gutter-spacing, 'fifteen');

        div.form-group {
            margin-bottom: 0;
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'sm')) {
    .module-participant-table ::v-deep {
        .options {
            flex-direction: column;
            align-items: flex-start;

            > div.form-group,
            > button.btn {
                width: 100%;
            }
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .module-participant-table ::v-deep {
        .options {
            flex-direction: row;
            align-items: flex-end;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: map-get($grid-gutter-spacing, 'ten') $grid-gutter-width;

            > div.form-group,
            > button.btn {
                width: auto;
                flex: 1 1 auto;
            }
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'xl')) {
    .module-participant-table ::v-deep {
        div.apprentice-table-header {
            flex-direction: column;
        }

        .options {
            justify-content: flex-end;
            margin-bottom: 0;

            > div.form-group {
                flex: 0 1 270px;
            }

            > button.btn {
                flex: 0 1 140px;
            }
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'xxl')) {
    .module-participant-table ::v-deep {
        div.apprentice-table-header {
            flex-direction: row;
            column-gap: map-get($grid-gutter-spacing, 'fifteen');

            div.apprentice-table-filters {
                align-items: flex-end;
            }

            .search {
                flex-grow: 0;
            }

            div.page-records {
                width: max-content;
            }
        }

        .options {
            flex: 1 1 auto;
            margin: 0;

            > div.form-group {
                flex: 0 1 250px;
            }
        }
    }
}
</style>
